import React, { useState, useEffect, useRef } from 'react';
import type { User, UserStory, StoryItem } from '../types';
import XMarkIcon from './icons/XMarkIcon';
import TrashIcon from './icons/TrashIcon';
import OverlayLayer from './OverlayLayer';

interface StoryViewerProps {
  stories: UserStory[];
  startIndex: number;
  onClose: () => void;
  onViewProfile: (user: User) => void;
  currentUserId?: string;
  // Swipe up on someone else's news to reply: sends a chat message to whoever posted it.
  onReplyToNews?: (ownerId: string, item: StoryItem, text: string) => Promise<void>;
  onDeleteItem?: (ownerId: string, itemId: string) => Promise<void>;
}

/**
 * Instagram-style story viewer with tap-to-advance, progress bars, and swipe-down to close.
 */
const StoryViewer: React.FC<StoryViewerProps> = ({ stories: allStories, startIndex, onClose, onViewProfile, currentUserId, onDeleteItem, onReplyToNews }) => {
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyState, setReplyState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const replyInputRef = useRef<HTMLInputElement>(null);
  const stories = deletedIds.length
    ? allStories.map(s => ({ ...s, items: (s.items || []).filter(i => !deletedIds.includes(i.id)) }))
        .filter((s, i) => s.items.length > 0 || (allStories[i].items || []).every(it => !deletedIds.includes(it.id)))
    : allStories;
  if (!stories || stories.length === 0) return null;
  const [currentUserIndex, setCurrentUserIndex] = useState(Math.min(Math.max(startIndex, 0), stories.length - 1));
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const originalBodyOverflow = useRef<string | null>(null);
  const originalHtmlOverflow = useRef<string | null>(null);
  const touchStartY = useRef<number | null>(null);

  const currentUserStory = stories[currentUserIndex];
  const items = (currentUserStory.items || []).length > 0 ? currentUserStory.items : [{
    id: `placeholder_${currentUserStory.user.id || currentUserStory.user.username || 'story'}`,
    type: 'image' as const,
    url: currentUserStory.user.avatarUrl || '/logo.png',
    duration: 5,
    timestamp: Date.now(),
  }];
  const currentItem = items[Math.min(currentItemIndex, items.length - 1)];

  const goToNextStory = () => {
    setProgress(0);
    setDuration(null);
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else if (currentUserIndex < stories.length - 1) {
      setCurrentUserIndex(prev => prev + 1);
      setCurrentItemIndex(0);
      setIsPaused(false);
    } else {
      closeWithAnimation();
    }
  };

  const goToPrevStory = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(prev => prev - 1);
    } else if (currentUserIndex > 0) {
      const prevUserIndex = currentUserIndex - 1;
      setCurrentUserIndex(prevUserIndex);
      const prevItems = (stories[prevUserIndex].items || []).length > 0 ? stories[prevUserIndex].items : [{
        id: `placeholder_${stories[prevUserIndex].user.id || stories[prevUserIndex].user.username || 'story'}`,
        type: 'image' as const,
        url: stories[prevUserIndex].user.avatarUrl || '/logo.png',
        duration: 5,
        timestamp: Date.now(),
      }];
      setCurrentItemIndex(prevItems.length - 1);
    }
  };

  const canDelete = !!onDeleteItem && !!currentUserId && currentUserStory.user.id === currentUserId && (currentUserStory.items || []).length > 0;

  const handleDelete = async () => {
    if (!onDeleteItem || isDeleting) return;
    setIsPaused(true);
    if (!window.confirm('Delete this news? This can\'t be undone.')) { setIsPaused(false); return; }
    setIsDeleting(true);
    try {
      await onDeleteItem(currentUserStory.user.id, currentItem.id);
      setDeletedIds(prev => [...prev, currentItem.id]);
      // Deleting your news ends the viewing session: close the viewer (the app then lands on the home page).
      closeWithAnimation();
      return;
    } catch (err) {
      console.error('Failed to delete news', err);
      window.alert('Could not delete this news. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsPaused(false);
    }
  };

  const handleViewProfile = () => {
    onViewProfile(currentUserStory.user);
    closeWithAnimation();
  };

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setProgress(0);
    setDuration(null);
    startTimeRef.current = performance.now();

    const run = (now: number) => {
      const baseDuration = currentItem.type === 'image'
        ? (currentItem.duration || 5)
        : (duration || videoRef.current?.duration || 0);

      if (!baseDuration) {
        rafRef.current = requestAnimationFrame(run);
        return;
      }

      const elapsed = (videoRef.current && currentItem.type === 'video')
        ? videoRef.current.currentTime
        : (now - startTimeRef.current) / 1000;

      const pct = Math.min(1, Math.max(0, elapsed / baseDuration));
      if (!isPaused) setProgress(pct * 100);

      if (!isPaused && pct >= 1) {
        goToNextStory();
        return;
      }
      rafRef.current = requestAnimationFrame(run);
    };

    if (!isPaused) {
      rafRef.current = requestAnimationFrame(run);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [currentItemIndex, currentUserIndex, isPaused, duration]);

  const handleVideoEnded = () => {
    goToNextStory();
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current?.duration) {
      setDuration(videoRef.current.duration);
      setProgress(0);
      startTimeRef.current = performance.now();
    }
  };

  const handleTap = (e: React.MouseEvent<HTMLDivElement>) => {
    const { clientX, currentTarget } = e;
    const { left, width } = currentTarget.getBoundingClientRect();
    const tapPosition = (clientX - left) / width;
    if (tapPosition > 0.5) {
      goToNextStory();
    } else {
      goToPrevStory();
    }
  };

  useEffect(() => {
    const timer = requestAnimationFrame(() => setIsVisible(true));
    originalBodyOverflow.current = document.body.style.overflow;
    originalHtmlOverflow.current = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      cancelAnimationFrame(timer);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      document.body.style.overflow = originalBodyOverflow.current ?? '';
      document.documentElement.style.overflow = originalHtmlOverflow.current ?? '';
    };
  }, []);

  const closeWithAnimation = () => {
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(onClose, 280);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isReplying) return;
    touchStartY.current = e.touches[0].clientY;
    setIsPaused(true);
  };

  const canReply = !!onReplyToNews && !!currentUserId && currentUserStory.user.id !== currentUserId && (currentUserStory.items || []).length > 0;

  const openReply = () => {
    if (!canReply) return;
    setIsReplying(true);
    setIsPaused(true);
    // Focus synchronously inside the gesture so iOS actually raises the keyboard.
    replyInputRef.current?.focus();
  };

  const closeReply = () => {
    setIsReplying(false);
    setIsPaused(false);
    setReplyText('');
    replyInputRef.current?.blur();
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !onReplyToNews || replyState === 'sending') return;
    setReplyState('sending');
    try {
      await onReplyToNews(currentUserStory.user.id, currentItem, text);
      setReplyState('sent');
      setReplyText('');
      replyInputRef.current?.blur();
      window.setTimeout(() => { setReplyState('idle'); setIsReplying(false); setIsPaused(false); }, 1100);
    } catch (err) {
      console.error('Failed to reply to news', err);
      setReplyState('idle');
      window.alert('Could not send your reply. Please try again.');
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isReplying) return;
    setIsPaused(false);
    const start = touchStartY.current;
    touchStartY.current = null;
    if (start !== null) {
      const delta = e.changedTouches[0].clientY - start;
      if (delta > 80) {
        closeWithAnimation();
      } else if (delta < -60) {
        openReply();
      }
    }
  };

  return (
    <div className="relative w-full h-full bg-app-backdrop flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0"
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      ></div>

      <div className={`w-full h-full relative overflow-hidden bg-app-backdrop transform transition-all duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
        {/* Media */}
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {currentItem.type === 'image' ? (
            <img src={currentItem.url} className="w-full h-full object-contain" alt="Story content" />
          ) : (
            <video
              ref={videoRef}
              key={currentItem.url}
              src={currentItem.url}
              className="w-full h-full object-contain bg-black"
              autoPlay
              playsInline
              muted
              onEnded={handleVideoEnded}
              onLoadedMetadata={handleLoadedMetadata}
            />
          )}
          <OverlayLayer overlays={currentItem.overlays} fit="contain" />
        </div>

        {/* UI Overlay */}
        <div className="absolute inset-0 flex flex-col">
          {/* Top gradient + progress */}
          <div
            className="bg-gradient-to-b from-[#25282d]/80 via-[#25282d]/35 to-transparent pb-2 px-3"
            style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 1rem)' }}
          >
            <div className="flex space-x-1 mb-2">
              {currentUserStory.items.map((item, index) => (
                <div key={item.id} className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-[width] duration-120 ease-linear"
                    style={
                      index < currentItemIndex
                        ? { width: '100%' }
                        : index === currentItemIndex
                        ? { width: `${progress}%` }
                        : { width: '0%' }
                    }
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <button onClick={handleViewProfile} className="flex items-center focus:outline-none focus:ring-2 focus:ring-lime-500 rounded-lg p-1 -ml-1">
                <img src={currentUserStory.user.avatarUrl} alt={currentUserStory.user.username} className="w-10 h-10 rounded-xl object-cover" />
                <span className="ml-3 font-bold text-white drop-shadow-sm">{currentUserStory.user.username}</span>
              </button>
              <div className="flex flex-col items-end gap-2">
                <button onClick={closeWithAnimation} className="p-2 text-white bg-app-overlay rounded-full">
                  <XMarkIcon className="w-6 h-6" />
                </button>
                {canDelete && (
                  <button onClick={handleDelete} disabled={isDeleting} className="p-2 text-white bg-app-overlay rounded-full disabled:opacity-50" aria-label="Delete this news">
                    <TrashIcon className="w-6 h-6" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Taps */}
          <div className="flex-grow w-full relative" onClick={handleTap}>
            <div className="absolute inset-0 flex">
              <div className="w-1/3 h-full" onClick={(e) => { e.stopPropagation(); goToPrevStory(); }} />
              <div className="w-1/3 h-full" />
              <div className="w-1/3 h-full" onClick={(e) => { e.stopPropagation(); goToNextStory(); }} />
            </div>
          </div>
          {/* Reply composer: swipe up (or tap the bar) to message whoever posted this news. */}
          {canReply && (
            <div
              className="keyboard-avoiding-bar relative z-10 px-3"
              style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 0.75rem)' }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {replyState === 'sent' ? (
                <p className="rounded-full bg-white px-5 py-3.5 text-center text-sm font-black text-black">Sent to {'@' + String(currentUserStory.user.username).toUpperCase()}</p>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); void sendReply(); }}
                  className={`flex items-center gap-2 rounded-full border bg-black/55 py-1.5 pl-5 pr-1.5 backdrop-blur-md ${isReplying ? 'border-white' : 'border-white/40'}`}
                >
                  <input
                    ref={replyInputRef}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                    onFocus={() => { setIsReplying(true); setIsPaused(true); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') closeReply(); }}
                    placeholder={`Send a message to @${currentUserStory.user.username}…`}
                    aria-label="Reply to news"
                    enterKeyHint="send"
                    className="min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-white/70 focus:outline-none"
                  />
                  {isReplying && (
                    <button type="button" onClick={closeReply} className="shrink-0 px-2 text-sm font-bold text-white/70">Cancel</button>
                  )}
                  <button
                    type="submit"
                    disabled={!replyText.trim() || replyState === 'sending'}
                    className="shrink-0 rounded-full bg-white px-4 py-2 text-sm font-black text-black disabled:opacity-40"
                  >
                    {replyState === 'sending' ? '…' : 'Send'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;
